import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import { PersonalisationLoader } from '../PersonalisationLoader/index';

import styles from './location-map-generator.module.css';

// Geoapify style URL for MapLibre GL JS
function getGeoapifyStyleUrl(apiKey) {
  return `https://maps.geoapify.com/v1/styles/osm-bright/style.json?apiKey=${apiKey}`;
}

// Geoapify vector tile URL pattern (for custom B&W style source)
function getGeoapifyVectorTileUrl(apiKey) {
  return `https://maps.geoapify.com/v1/tile/vector/{z}/{x}/{y}.pbf?apiKey=${apiKey}`;
}

/**
 * Wait for map style to load and render, with timeout protection.
 * Prevents hanging forever if map resources fail to load.
 *
 * @param {any} map
 * @param {number} timeoutMs
 * @returns {Promise<void>}
 */
/**
 * Wait for map to finish loading style and rendering tiles after a setStyle() call.
 * Uses the 'idle' event which fires when the map finishes rendering all pending
 * operations (style changes + tile loading). MapLibre GL JS v4 does not reliably
 * emit 'style.load' for inline style objects, so we listen for 'idle' instead.
 *
 * @param {any} map
 * @param {number} timeoutMs
 * @returns {Promise<void>}
 */
function waitForStyleLoadAndIdle(map, timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      clearTimeout(timeoutId);
      map.off('error', onError);
    };

    const settleResolve = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    const settleReject = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const onError = (event) => {
      const message = event?.error?.message || 'Map failed while loading style';
      settleReject(new Error(message));
    };

    const timeoutId = setTimeout(() => {
      settleReject(new Error('Timed out waiting for map style to load'));
    }, timeoutMs);

    map.on('error', onError);

    if (map.loaded()) {
      settleResolve();
    } else {
      map.once('idle', () => {
        settleResolve();
      });
    }
  });
}

/**
 * Wait for map idle state with timeout protection.
 *
 * @param {any} map
 * @param {number} timeoutMs
 * @returns {Promise<void>}
 */
function waitForMapIdle(map, timeoutMs = 10000) {
  return new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      clearTimeout(timeoutId);
      map.off('error', onError);
    };

    const settleResolve = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    const settleReject = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const onError = (event) => {
      const message = event?.error?.message || 'Map failed while waiting for render';
      settleReject(new Error(message));
    };

    const timeoutId = setTimeout(() => {
      settleReject(new Error('Timed out waiting for map render to finish'));
    }, timeoutMs);

    map.on('error', onError);
    if (map.loaded()) {
      settleResolve();
      return;
    }

    map.once('idle', () => {
      settleResolve();
    });
  });
}

/**
 * @typedef {Object} LocationMapGeneratorRef
 * @property {function(): Promise<string | null>} generateMapImage - Generates the B&W map image
 * @property {function(): boolean} isGenerating - Returns whether image is currently generating
 * @property {function(): boolean} hasImage - Returns whether image has been generated
 */

/**
 * LocationMapGenerator component generates and displays a location map based on provided location data.
 *
 * @param {Object} props - The component props
 * @param {Object} props.locationData - The location data object with location, locationPlace
 * @param {string} [props.serviceUnavailableMessage] - Message shown when location services are unavailable
 * @param {Function} [props.onGenerate] - Optional callback when generation starts
 * @param {Function} [props.onSuccess] - Optional callback when generation succeeds
 * @param {Function} [props.onError] - Optional callback when generation fails
 * @param {React.Ref<LocationMapGeneratorRef>} ref - Ref to expose generateMapImage method
 * @returns {import('react').JSX.Element} The rendered component
 */
/**
 * @typedef {Object} LocationMapGeneratorProps
 * @property {Object} locationData - The location data object with location, locationPlace
 * @property {string} [serviceUnavailableMessage] - Message shown when location services are unavailable
 * @property {Function} [onGenerate] - Optional callback when generation starts
 * @property {Function} [onSuccess] - Optional callback when generation succeeds
 * @property {Function} [onError] - Optional callback when generation fails
 */

export const LocationMapGenerator = forwardRef(
  /**
   * @param {LocationMapGeneratorProps} props
   * @param {React.Ref<LocationMapGeneratorRef>} ref
   */
  ({ locationData, serviceUnavailableMessage = '', onGenerate, onSuccess, onError }, ref) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [isMapLoading, setIsMapLoading] = useState(false);
    const [locationMapImageUrl, setLocationMapImageUrl] = useState(/** @type {string | null} */ (null));
    const [error, setError] = useState(/** @type {string | null} */ (null));
    const hasGeneratedRef = useRef(false);

    const mapContainerRef = useRef(/** @type {HTMLDivElement | null} */ (null));
    const mapRef = useRef(/** @type {any} */ (null));
    const maplibreLoadedRef = useRef(false);
    const apiKeyRef = useRef(/** @type {string | null} */ (null));

    // Fixed circle radius
    const circleRadius = 150;

    // Load MapLibre GL JS
    useEffect(() => {
      if (maplibreLoadedRef.current) return;

      // Check if MapLibre is already loaded
      // @ts-expect-error - MapLibre types not available
      if (window.maplibregl) {
        maplibreLoadedRef.current = true;
        return;
      }

      // Load MapLibre CSS
      const link = document.createElement('link');
      link.href = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);

      // Load MapLibre JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js';
      script.async = true;
      script.onload = () => {
        maplibreLoadedRef.current = true;
        setTimeout(() => {
          if (locationData?.locationPlace?.location) {
            initializeMap();
          }
        }, 100);
      };
      script.onerror = () => {
        const errorMsg = 'Failed to load MapLibre GL JS';
        setError(errorMsg);
        setIsMapLoading(false);
        if (onError) {
          onError(errorMsg);
        }
      };
      document.head.appendChild(script);

      return () => {
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      };
    }, []);

    // Initialize MapLibre map with Geoapify tiles
    const initializeMap = useCallback(() => {
      // @ts-expect-error - MapLibre types not available
      if (!window.maplibregl || !mapContainerRef.current || mapRef.current) {
        return;
      }
      setIsMapLoading(true);

      // Get Geoapify API key from environment variable
      const geoapifyApiKey = import.meta.env.VITE_GEOAPIFY_API_KEY;

      if (!geoapifyApiKey) {
        const errorMsg = 'Geoapify API key not found. Please set VITE_GEOAPIFY_API_KEY environment variable.';
        setError(errorMsg);
        if (onError) {
          onError(errorMsg);
        }
        return;
      }

      // Store API key in ref for use in effects
      apiKeyRef.current = geoapifyApiKey;

      const lat = locationData?.locationPlace?.location?.lat || 40;
      const lng = locationData?.locationPlace?.location?.lng || -74.5;

      // @ts-expect-error - MapLibre types not available
      const map = new window.maplibregl.Map({
        container: mapContainerRef.current,
        style: getGeoapifyStyleUrl(geoapifyApiKey),
        center: [lng, lat],
        zoom: 15,
        preserveDrawingBuffer: true,
        attributionControl: false
      });

      // Set min zoom to ensure lines are at least 1mm (approximately zoom level 15-16)
      map.setMinZoom(15);
      map.setMaxZoom(20);

      mapRef.current = map;

      // Mark map ready after initial render settles.
      map.on('load', () => {
        map.once('idle', () => {
          setIsMapLoading(false);
        });
      });
    }, [locationData, onError]);

    // Initialize map when location data is available
    useEffect(() => {
      if (maplibreLoadedRef.current && locationData?.locationPlace?.location && !mapRef.current) {
        setIsMapLoading(true);
        const timer = setTimeout(() => {
          initializeMap();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [locationData, initializeMap]);

    // Reset map when location changes
    useEffect(() => {
      if (mapRef.current && locationData?.locationPlace?.location) {
        setIsMapLoading(true);
        const lat = locationData.locationPlace.location.lat;
        const lng = locationData.locationPlace.location.lng;

        // Reset to Geoapify style and fly to new location
        mapRef.current.setStyle(getGeoapifyStyleUrl(apiKeyRef.current));
        mapRef.current.flyTo({
          center: [lng, lat],
          zoom: 12,
          duration: 2000
        });
        mapRef.current.once('idle', () => {
          setIsMapLoading(false);
        });

        // Reset state
        setLocationMapImageUrl(null);
        setError(null);
        hasGeneratedRef.current = false;
      }
    }, [locationData?.locationPlace?.location?.lat, locationData?.locationPlace?.location?.lng]);

    // Generate B&W map image
    const generateMapImage = useCallback(async () => {
      if (!mapRef.current) {
        return;
      }

      setIsGenerating(true);
      setError(null);

      if (onGenerate) {
        onGenerate();
      }

      try {
        const map = mapRef.current;
        const mapElement = mapContainerRef.current;

        if (!mapElement) {
          throw new Error('Map container not found');
        }

        // Apply circular clip path to map element
        mapElement.style.clipPath = `circle(${circleRadius}px at center)`;
        // @ts-expect-error - webkitClipPath is a valid CSS property but not in TypeScript types
        mapElement.style.webkitClipPath = `circle(${circleRadius}px at center)`;

        // Change map style to minimal B&W roads only (no road names)
        // Uses Geoapify vector tiles with OpenMapTiles schema (transportation layer)
        map.setStyle({
          version: 8,
          sources: {
            openmaptiles: {
              type: 'vector',
              tiles: [getGeoapifyVectorTileUrl(apiKeyRef.current)],
              maxzoom: 14
            }
          },
          layers: [
            {
              id: 'background',
              type: 'background',
              paint: {
                'background-color': '#000000'
              }
            },
            // Include ALL roads with varying line widths based on road class
            // OpenMapTiles uses 'transportation' source-layer with similar class values
            // Major roads are thicker, minor roads are thinner, but all meet minimum 1mm requirement
            {
              id: 'roads-major',
              type: 'line',
              source: 'openmaptiles',
              'source-layer': 'transportation',
              filter: ['in', 'class', 'motorway', 'trunk', 'primary'],
              paint: {
                'line-color': '#ffffff',
                'line-width': [
                  'interpolate',
                  ['exponential', 1.5],
                  ['zoom'],
                  10,
                  2, // At zoom 10
                  15,
                  4, // At zoom 15
                  20,
                  10 // At zoom 20
                ]
              }
            },
            {
              id: 'roads-secondary',
              type: 'line',
              source: 'openmaptiles',
              'source-layer': 'transportation',
              filter: ['==', 'class', 'secondary'],
              paint: {
                'line-color': '#ffffff',
                'line-width': [
                  'interpolate',
                  ['exponential', 1.5],
                  ['zoom'],
                  10,
                  1.5, // At zoom 10
                  15,
                  3, // At zoom 15
                  20,
                  7 // At zoom 20
                ]
              }
            },
            {
              id: 'roads-tertiary',
              type: 'line',
              source: 'openmaptiles',
              'source-layer': 'transportation',
              filter: ['==', 'class', 'tertiary'],
              paint: {
                'line-color': '#ffffff',
                'line-width': [
                  'interpolate',
                  ['exponential', 1.5],
                  ['zoom'],
                  10,
                  1.2, // At zoom 10
                  15,
                  2.5, // At zoom 15
                  20,
                  6 // At zoom 20
                ]
              }
            },
            {
              id: 'roads-street',
              type: 'line',
              source: 'openmaptiles',
              'source-layer': 'transportation',
              filter: ['in', 'class', 'minor', 'residential'],
              paint: {
                'line-color': '#ffffff',
                'line-width': [
                  'interpolate',
                  ['exponential', 1.5],
                  ['zoom'],
                  10,
                  1, // At zoom 10
                  15,
                  2, // At zoom 15
                  20,
                  5 // At zoom 20
                ]
              }
            },
            {
              id: 'roads-other',
              type: 'line',
              source: 'openmaptiles',
              'source-layer': 'transportation',
              // Catch-all for all other road types (motorway_link, construction, service, track, path, etc.)
              filter: [
                'all',
                ['has', 'class'],
                [
                  '!in',
                  'class',
                  'motorway',
                  'trunk',
                  'primary',
                  'secondary',
                  'tertiary',
                  'minor',
                  'residential',
                  'aerialway',
                  'ferry',
                  'rail',
                  'transit'
                ]
              ],
              paint: {
                'line-color': '#ffffff',
                'line-width': [
                  'interpolate',
                  ['exponential', 1.5],
                  ['zoom'],
                  10,
                  1, // At zoom 10 (minimum 1mm = 15px, but we'll scale this)
                  15,
                  1.8, // At zoom 15
                  20,
                  4 // At zoom 20
                ]
              }
            }
          ]
        });

        // Wait for style and render with timeout protection.
        await waitForStyleLoadAndIdle(map);
        await waitForMapIdle(map);

        const canvas = map.getCanvas();
        const width = canvas.width;
        const height = canvas.height;

        // Export at the map canvas native square resolution so line detail stays crisp.
        // Previous logic downsampled aggressively (based on fixed UI radius), softening roads.
        const sourceSize = Math.min(width, height);
        const sourceX = (width - sourceSize) / 2;
        const sourceY = (height - sourceSize) / 2;
        const canvasDiameter = sourceSize;
        const radiusPx = canvasDiameter / 2;

        // Create square canvas
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvasDiameter;
        tempCanvas.height = canvasDiameter;
        const ctx = tempCanvas.getContext('2d');

        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        // Ensure outside of the circular crop remains transparent.
        ctx.clearRect(0, 0, canvasDiameter, canvasDiameter);
        ctx.beginPath();
        ctx.arc(radiusPx, radiusPx, radiusPx, 0, Math.PI * 2);
        ctx.clip();

        // Draw the centered square map area at 1:1 resolution to avoid blur.
        ctx.drawImage(canvas, sourceX, sourceY, sourceSize, sourceSize, 0, 0, canvasDiameter, canvasDiameter);

        // TEMPORARILY DISABLED: Grayscale conversion to check for detail loss
        // Get image data without grayscale conversion
        const imageData = ctx.getImageData(0, 0, canvasDiameter, canvasDiameter);
        const data = imageData.data;

        // Create a copy for the inverted version
        const invertedData = new Uint8ClampedArray(data);

        for (let i = 0; i < data.length; i += 4) {
          // TEMPORARILY: Keep original colors (skip grayscale conversion)
          // Original version (for factory): keep as-is
          // data[i], data[i + 1], data[i + 2] remain unchanged

          // Inverted version (for display): invert RGB channels
          invertedData[i] = 255 - data[i]; // R
          invertedData[i + 1] = 255 - data[i + 1]; // G
          invertedData[i + 2] = 255 - data[i + 2]; // B
          invertedData[i + 3] = data[i + 3]; // Preserve alpha
        }

        // Put original version back (unchanged colors)
        ctx.putImageData(imageData, 0, 0);
        const originalDataUrl = tempCanvas.toDataURL('image/png');

        // Create inverted version
        const invertedImageData = new ImageData(invertedData, canvasDiameter, canvasDiameter);
        ctx.putImageData(invertedImageData, 0, 0);
        const invertedDataUrl = tempCanvas.toDataURL('image/png');

        setIsGenerating(false);
        hasGeneratedRef.current = true;
        setLocationMapImageUrl(invertedDataUrl); // Store inverted for display

        if (onSuccess) {
          // Pass both versions: { display: inverted, original: original }
          onSuccess({
            display: invertedDataUrl,
            original: originalDataUrl
          });
        }

        return invertedDataUrl;
      } catch (error) {
        console.error('Error generating map image:', error);
        const errorMessage = error.message || 'Failed to generate location map';
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
        setIsGenerating(false);
      }
    }, [circleRadius, onGenerate, onSuccess, onError]);

    // Expose generateMapImage method via ref
    useImperativeHandle(
      ref,
      () => ({
        generateMapImage: async () => {
          if (!mapRef.current) {
            return null;
          }

          if (isGenerating) {
            return locationMapImageUrl || null;
          }

          const imageUrl = await generateMapImage();
          return imageUrl || null;
        },
        isGenerating: () => isGenerating,
        hasImage: () => !!locationMapImageUrl
      }),
      [generateMapImage, locationMapImageUrl, isGenerating]
    );

    const handleZoomIn = useCallback(() => {
      if (mapRef.current) mapRef.current.zoomIn();
    }, []);

    const handleZoomOut = useCallback(() => {
      if (mapRef.current) mapRef.current.zoomOut();
    }, []);

    // Check if we have required data to generate a location map
    const canGenerateLocationMap =
      locationData?.locationPlace?.location?.lat && locationData?.locationPlace?.location?.lng;

    useEffect(() => {
      if (!canGenerateLocationMap) {
        setIsMapLoading(false);
      }
    }, [canGenerateLocationMap]);

    return (
      <div
        className={`${styles['location-map-generator']} ${serviceUnavailableMessage ? styles['location-map-generator--error'] : ''}`}
      >
        {canGenerateLocationMap && (
          <div className={styles['location-map-generator__status']}>
            {error && <p className={styles['location-map-generator__error']}>Error: {error}</p>}

            <div className={styles['location-map-generator__map-wrapper']}>
              <div className={styles['location-map-generator__map-container']}>
                <div ref={mapContainerRef} className={styles['location-map-generator__map']} />
                {isMapLoading && (
                  <div className={styles['location-map-generator__map-loading']}>
                    <PersonalisationLoader isVisible={true} />
                  </div>
                )}
              </div>
              <div className={styles['location-map-generator__zoom-controls']}>
                <button
                  type="button"
                  className={styles['location-map-generator__zoom-btn']}
                  onClick={handleZoomIn}
                  aria-label="Zoom in"
                >
                  +
                </button>
                <button
                  type="button"
                  className={styles['location-map-generator__zoom-btn']}
                  onClick={handleZoomOut}
                  aria-label="Zoom out"
                >
                  &minus;
                </button>
              </div>
              <p className={`${styles['location-map-generator__attribution']} body-xs`}>
                Powered by{' '}
                <a href="https://www.geoapify.com/" target="_blank" rel="noopener noreferrer">
                  Geoapify
                </a>{' '}
                |{' '}
                <a href="https://openmaptiles.org/" target="_blank" rel="noopener noreferrer">
                  &copy; OpenMapTiles
                </a>
                |{' '}
                <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
                  &copy; OpenStreetMap
                </a>{' '}
                contributors
              </p>
            </div>

            {isGenerating && (
              <div className={styles['location-map-generator__loading']}>
                <PersonalisationLoader isVisible={true} />
                <p className="body-sm">Preparing your map...</p>
              </div>
            )}
          </div>
        )}

        {!canGenerateLocationMap && (
          <p className={`body-sm font-regular ${styles['location-map-generator__message']}`}>
            {serviceUnavailableMessage || 'Please provide a location to generate a location map.'}
          </p>
        )}

        {canGenerateLocationMap && locationData?.locationPlace && (
          <div className={styles['location-map-generator__data']}>
            {locationData.locationPlace.formattedAddress && (
              <p className="body-sm font-regular">{locationData.locationPlace.formattedAddress}</p>
            )}
          </div>
        )}
      </div>
    );
  }
);
